import type { CityConfig } from "@/pages/city-template/types";

export const roseburgConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Douglas County Clerk Vital Records", desc: "Obtain vital records through the Douglas County Clerk's office in Roseburg.", url: "https://www.co.douglas.or.us/clerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Roseburg",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "roseburg",
  route: "/roseburg",
  title: "Emergency Passport Roseburg OR | Expedited Passport Douglas County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Roseburg, OR — as fast as 24 hours. Fast Passport Center serves Douglas County, the Umpqua Valley, Sutherlin, and Winston. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Roseburg, Oregon",
  heroSubtitle: "Passport expired before your British Columbia hunting trip? Family cruise departing Seattle and your passport is outdated? Fast Passport Center helps Roseburg residents get expedited passports as fast as 24 hours — no 6-hour drive to Seattle required. A+ BBB rated. Registered State Dept. courier.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Roseburg%20Oregon%20scenic%20Umpqua%20River%20valley%20surrounded%20by%20rolling%20Coast%20Range%20forested%20hills%20and%20vineyards%2C%20golden%20afternoon%20light%20illuminating%20the%20lush%20green%20valley%20floor%20with%20Douglas%20fir%20forests%20on%20ridgelines%2C%20wide%20panoramic%20Pacific%20Northwest%20landscape%20photography%2C%20warm%20tones%2C%20clear%20skies%2C%20no%20text%20no%20people%20visible&width=1800&height=700&seq=roseburg-or-hero-01&orientation=landscape",
  heroImageAlt: "Roseburg Oregon with the Umpqua River valley surrounded by the Umpqua Valley wine country and Coast Range forests",
  geoPosition: "43.2165;-123.3564",
  geoPlacename: "Roseburg, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 380 miles north — approximately 6 hours by car via I-5",
  },
  nearbyCitiesZips: [
    { name: "Roseburg", zips: "97470, 97471" },
    { name: "Sutherlin", zips: "97479" },
    { name: "Winston", zips: "97496" },
    { name: "Myrtle Creek", zips: "97457" },
    { name: "Canyonville", zips: "97417" },
    { name: "Riddle", zips: "97469" },
    { name: "Dillard", zips: "97432" },
    { name: "Glide", zips: "97443" },
    { name: "Oakland", zips: "97462" },
    { name: "Camas Valley", zips: "97416" },
    { name: "Drain", zips: "97435" },
    { name: "Myrtle Point", zips: "97458" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Roseburg", desc: "Complete your emergency or expedited passport order from Roseburg — no 6-hour drive to Seattle required." },
    { icon: "ri-truck-line", title: "Ship Overnight from Roseburg", desc: "FedEx and UPS locations in Roseburg serve the entire Umpqua Valley for overnight passport document shipping." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — essential for Douglas County applicants far from regional agencies." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment system with reserved Seattle Passport Agency slots — no lengthy drive required." },
  ],
  testimonials: [
    {
      name: "Tom K.",
      location: "Roseburg, OR",
      rating: 5,
      text: "Elk hunting trip to British Columbia with my dad — passport expired during COVID. Fast Passport Center renewed it in 10 days. Shipped FedEx from the Roseburg UPS Store and it came back to my door in time. No drama, no Seattle drive. Outstanding service.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Karen L.",
      location: "Sutherlin, OR",
      rating: 5,
      text: "Our family Mexico vacation was three weeks out when I discovered my kids' passports had expired. Fast Passport Center renewed both in 9 days while I was juggling work and school pickups. Completely hands-off on my end after shipping. Incredible.",
      service: "Child Passport",
    },
    {
      name: "Mark R.",
      location: "Roseburg, OR",
      rating: 5,
      text: "Umpqua Valley wine industry connection got me invited to a French wine region partnership trip on 10 days notice. Fast Passport Center got my renewal done in 7 days. I was amazed. Great communication throughout.",
      service: "Expedited Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "roseburg-post-main",
      name: "Roseburg Main Post Office",
      type: "USPS Passport Acceptance",
      address: "920 NW Garden Valley Blvd",
      city: "Roseburg",
      state: "OR",
      zip: "97471",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 10am–2pm",
      distance: "North Roseburg — primary passport acceptance location for Douglas County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Roseburg residents. Appointment required — book well in advance. Saturday morning passport hours available.",
      icon: "ri-mail-line",
    },
    {
      id: "roseburg-post-downtown",
      name: "Roseburg Downtown Post Office",
      type: "USPS Passport Acceptance",
      address: "728 SE Stephens St",
      city: "Roseburg",
      state: "OR",
      zip: "97470",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Roseburg — secondary location for southeast Roseburg residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Downtown Roseburg location. Check appointment availability — may be more convenient for southeast Roseburg residents.",
      icon: "ri-mail-line",
    },
    {
      id: "douglas-county-clerk",
      name: "Douglas County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "1036 SE Douglas Ave",
      city: "Roseburg",
      state: "OR",
      zip: "97470",
      phone: "(541) 440-4300",
      hours: "Mon–Fri 8am–5pm",
      distance: "Douglas County Courthouse — downtown Roseburg",
      website: "https://www.co.douglas.or.us/clerk",
      websiteLabel: "Douglas County Clerk",
      notes: "Douglas County Clerk accepts passport applications. Generally reliable appointment availability as an alternative to the post office.",
      icon: "ri-government-line",
    },
    {
      id: "sutherlin-post",
      name: "Sutherlin Post Office",
      type: "USPS Passport Acceptance",
      address: "155 E Central Ave",
      city: "Sutherlin",
      state: "OR",
      zip: "97479",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Sutherlin — about 12 miles north of Roseburg",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Sutherlin post office for north Douglas County residents. Limited appointment availability — Roseburg Main Post Office preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "winston-post",
      name: "Winston Post Office",
      type: "USPS Passport Acceptance",
      address: "201 NE Douglas Blvd",
      city: "Winston",
      state: "OR",
      zip: "97496",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Winston — about 7 miles south of Roseburg",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Winston post office for South Umpqua area residents. Limited availability — Roseburg preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "myrtle-creek-post",
      name: "Myrtle Creek Post Office",
      type: "USPS Passport Acceptance",
      address: "711 Maple St",
      city: "Myrtle Creek",
      state: "OR",
      zip: "97457",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Myrtle Creek — about 20 miles south of Roseburg via I-5",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "South Douglas County option for Myrtle Creek and Canyonville area residents. Very limited availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Roseburg, Oregon?",
      a: "Yes. Through Fast Passport Center, Roseburg residents can get an emergency passport in as fast as 24 hours for urgent travel, or 2–9 business days with expedited processing. The nearest regional passport agency (Seattle) is 6 hours north — we serve Roseburg entirely remotely. Start online, ship overnight from the Roseburg FedEx or UPS Store, and we submit through reserved Seattle agency channels.",
    },
    {
      q: "How fast can I renew my passport from Roseburg?",
      a: "Expedited passport renewal from Roseburg is available in as little as 3 business days through Fast Passport Center. Most adult renewals do not require an in-person acceptance visit. Ship overnight from the Roseburg FedEx Office or UPS Store — we handle the agency submission.",
    },
    {
      q: "Does Roseburg have a passport agency?",
      a: "Roseburg does not have a regional passport agency. The nearest is the Seattle Passport Agency, approximately 380 miles north (6-hour drive). For Roseburg-area residents, Fast Passport Center provides the practical alternative — start online, ship overnight, no Seattle trip required.",
    },
    {
      q: "Where do Roseburg residents submit passport applications?",
      a: "Passport acceptance locations near Roseburg include the Roseburg Main Post Office (920 NW Garden Valley Blvd), the Douglas County Clerk (1036 SE Douglas Ave), and the Downtown Post Office (728 SE Stephens St). Sutherlin and Winston post offices have very limited availability.",
    },
    {
      q: "Can I expedite a passport for a British Columbia hunting or fishing trip from Roseburg?",
      a: "Yes. BC hunting and fishing trips are the most common passport urgency for Roseburg-area residents. Fast Passport Center handles expedited renewals as fast as 3 business days for adult renewal-eligible applicants. Call us as soon as you have your BC license or outfitter booking confirmed.",
    },
    {
      q: "Can I get a passport expedited for the Umpqua Valley wine industry travel from Roseburg?",
      a: "Yes. The Umpqua Valley is Oregon's oldest wine growing region, and wine professionals frequently travel to France, Italy, and Germany for partnerships and education. Fast Passport Center handles expedited passports for Roseburg wine industry professionals with the urgency required for fixed conference and harvest dates.",
    },
    {
      q: "How long does standard passport processing take from Roseburg?",
      a: "Standard USPS mail processing from Roseburg runs 6–13 weeks during normal periods, and longer during peak summer. If your trip is within 10 weeks, expedited service through Fast Passport Center is strongly recommended.",
    },
    {
      q: "What airport do Roseburg residents use for international travel?",
      a: "Roseburg has the Roseburg Regional Airport (RBG) for small aircraft. For commercial international travel, residents typically drive to Eugene Airport (EUG, 65 miles north) or Portland International (PDX, 180 miles north). PDX offers direct international service to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City.",
    },
    {
      q: "Can I renew a passport without visiting an office in Roseburg?",
      a: "Yes. Most adult Roseburg residents renewing a passport can do so entirely by mail — no in-person acceptance visit required. Start online with Fast Passport Center, complete documents at home, and ship overnight from the Roseburg FedEx or UPS Store.",
    },
    {
      q: "Can Umpqua Community College students get expedited passports from Roseburg?",
      a: "Yes. Fast Passport Center helps Umpqua Community College students and other Roseburg-area residents get expedited passports for study abroad programs, international travel, and travel emergencies. Start your order as soon as your travel is confirmed.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Roseburg", address: "2675 NE Stephens St, Roseburg, OR 97470", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Roseburg", address: "2125 NW Stewart Pkwy, Roseburg, OR 97471", hours: "Daily 7 AM – 11 PM" },
    { name: "FedEx Office — Roseburg", address: "2477 NE Diamond Lake Blvd, Roseburg, OR 97470", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 5 PM" },
    { name: "The UPS Store — Roseburg", address: "1450 NW Garden Valley Blvd, Roseburg, OR 97471", hours: "Mon–Fri 8:30 AM – 6:30 PM, Sat 10 AM – 4 PM" },
    { name: "Fred Meyer — Roseburg", address: "2255 NW Stewart Pkwy, Roseburg, OR 97471", hours: "Daily 7 AM – 11 PM" },
    { name: "Rite Aid — Roseburg", address: "2335 NE Stephens St, Roseburg, OR 97470", hours: "Daily 8 AM – 10 PM" },
    { name: "Office Depot — Roseburg", address: "1505 NW Mulholland Dr, Roseburg, OR 97471", hours: "Mon–Sat 8 AM – 8 PM, Sun 10 AM – 6 PM" },
    { name: "Walgreens — Eugene North", address: "1965 NW Monroe Ave, Corvallis, OR 97330", hours: "Daily 8 AM – 10 PM" },
    { name: "FedEx Office — Eugene", address: "66 E Broadway, Eugene, OR 97401", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Costco Photo Center — Eugene", address: "4550 Sports Way, Eugene, OR 97401", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
  ],
  dmvLocations: [
    {
      name: "Roseburg Driver & Motor Vehicle Services",
      address: "1783 NW Garden Valley Blvd, Roseburg, OR 97471",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Roseburg DMV — driver licenses, REAL ID, and ID cards for Douglas County residents.",
      featured: true,
    },
    {
      name: "Eugene Driver & Motor Vehicle Services",
      address: "200 Antelope Rd, White City, OR 97503",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Alternative for Douglas County residents — Lane County offices may have better appointment availability.",
      featured: false,
    },
    {
      name: "Sutherlin Driver & Motor Vehicle Services",
      address: "Sutherlin area — check Oregon DMV website",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "North Douglas County DMV service location.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "EUG",
      name: "Eugene Airport (Mahlon Sweet Field)",
      distance: "About 65 miles north via I-5",
      address: "28801 Douglas Dr, Eugene, OR 97402",
      note: "Eugene Airport is the primary commercial airport for Roseburg-area travelers, offering connections to Portland, San Francisco, Los Angeles, Seattle, and Denver for onward international flights. About a 1-hour drive north via I-5.",
      flightUrl: "https://www.eugeneairport.com/",
      flightLabel: "Eugene Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "About 180 miles north via I-5",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport is the nearest major international hub from Roseburg, offering direct service to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City. About a 3-hour drive north via I-5.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
    {
      code: "MFR",
      name: "Rogue Valley International–Medford Airport",
      distance: "About 75 miles south via I-5",
      address: "1000 Terminal Loop Pkwy, Medford, OR 97504",
      note: "Medford Airport is a southern alternative for Roseburg residents, with connections to San Francisco, Portland, and Seattle.",
      flightUrl: "https://www.jacksoncountyor.org/airport",
      flightLabel: "MFR Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Roseburg & Douglas County",
      description: "BC hunting trip confirmed last minute? Mexico vacation booked this week? Fast Passport Center handles emergency passport processing for Roseburg residents — as fast as 24 hours. Seattle is 6 hours away, but we serve you remotely.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired during the pandemic or years without international travel? Roseburg residents renew fast through Fast Passport Center — ship from local FedEx or UPS overnight, we handle the expedited agency submission.",
      bullets: ["Fastest renewal option for Roseburg residents", "Ship overnight from Roseburg FedEx or UPS Store", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in the Umpqua Valley or before an international trip? We guide Roseburg travelers through DS-64 and expedited replacement — no Seattle drive required.",
      bullets: ["DS-64 reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Douglas County Families",
      description: "Roseburg families traveling to British Columbia, Mexico, or internationally with children need child passports. We provide the checklist for the Douglas County Post Office or County Clerk acceptance step — you handle in-person, we expedite from there.",
      bullets: ["Both parents typically required to consent", "Roseburg Post Office or County Clerk guidance", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First-time passport from Roseburg? We guide you through the acceptance clerk step at the Roseburg Post Office or Douglas County Clerk and handle expedited processing after.",
      bullets: ["Step-by-step acceptance guidance", "Photo location guide for Roseburg and Douglas County", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at prices 30–100% lower than retail expediting services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Passport Services",
      description: "Name change after marriage, damaged passport replacement, second passports for Roseburg professionals and travelers — all with expedited courier access.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passport for frequent travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Roseburg, Oregon travelers with urgent international departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Roseburg and Douglas County residents needing passports faster than standard USPS timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Roseburg, Oregon residents — ship overnight from Roseburg FedEx or UPS Store, we handle the rest." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Roseburg travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Roseburg and Douglas County families heading to British Columbia, Mexico, or international destinations." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission serving Roseburg, Oregon and the Umpqua Valley area." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Until the Last Minute — Seattle Is 6 Hours Away", description: "The nearest regional passport agency is 6 hours north in Seattle. There is no practical walk-in option for Roseburg residents. Start Fast Passport Center's expedited service the day you book your trip — not the week before departure." },
    { icon: "ri-camera-off-line", title: "Wrong or Rejected Passport Photo", description: "Photos from grocery stores or drug stores frequently get rejected for lighting or expression issues. Fast Passport Center reviews photo specifications before submission to prevent these delays." },
    { icon: "ri-file-damage-line", title: "Incomplete or Missing Signatures", description: "One missing signature returns your application — costing weeks. Our pre-submission review catches all form errors before they become problems." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are typically rejected for passport applications. We specify exactly what document format you need based on your state of birth." },
    { icon: "ri-calendar-close-line", title: "Booking BC Trips Without Checking Passport", description: "Roseburg outdoor travelers book British Columbia hunting and fishing trips months in advance — often discovering passport issues only when packing. Check your passport the same day you book your BC trip." },
    { icon: "ri-map-pin-time-line", title: "Ignoring the Six-Month Validity Rule", description: "Mexico, Canada (by air), and most of Europe require your passport to be valid for six months beyond your return date. A passport expiring in three months is functionally expired for most international travel." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Roseburg travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with Roseburg-specific acceptance location recommendations." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Roseburg", description: "Drop off at the Roseburg FedEx Office or UPS Store — both serve Douglas County with overnight shipping to our processing center." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — no waiting for public appointment cancellations or 6-hour drives." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped Directly to Roseburg", description: "Your completed passport ships directly to your Roseburg or Umpqua Valley address. Track status in real time." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 6 hours each way",
        "Life-or-death emergencies only",
        "Very limited availability",
        "Not practical for Roseburg residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship from Roseburg FedEx or UPS Store",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; longer during peak summer",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (6 hours each way from Roseburg)", fpc: "Ship overnight from Roseburg FedEx or UPS Store" },
    { feature: "Processing Speed", agency: "Strict eligibility — urgent travel only", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Rural Accessibility", agency: "Not designed for Douglas County residents", fpc: "Fully remote — ship from Roseburg area" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 12-hour Seattle round trip expense", fpc: "Best price guarantee — no travel cost" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Eugene", route: "/eugene" },
    { name: "Emergency passport help in Medford", route: "/medford" },
    { name: "Fast passport renewal in Grants Pass", route: "/grants-pass" },
    { name: "Rush passport services in Ashland", route: "/ashland-or" },
    { name: "Expedited passports for Oregon travelers", route: "/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from the Roseburg FedEx Office or UPS Store — both open evenings. No visit to our processing facility required for most services.",
  fpcOfficeDistance: "Serving Roseburg and the entire Umpqua Valley area remotely",
  serviceAreaIntro: "Whether you are in Roseburg, Sutherlin, Winston, Myrtle Creek, Canyonville, Glide, or anywhere in Douglas County — Fast Passport Center makes emergency and expedited passport processing simple without the 6-hour drive to Seattle.",
  nearbyCitiesIntro: "We serve all Douglas County zip codes and surrounding communities including Sutherlin, Winston, Myrtle Creek, Canyonville, Riddle, Dillard, Glide, Oakland, Camas Valley, Drain, and Myrtle Point.",
  howItWorksIntro: "Getting an emergency or expedited passport from Roseburg is far simpler than a 12-hour Seattle round trip. Most Douglas County clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Roseburg and Douglas County passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Roseburg, Sutherlin, and Umpqua Valley travelers who needed their passports fast.",
  faqIntro: "The most common questions Roseburg and Douglas County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Roseburg and Umpqua Valley Residents",
    intro: "Roseburg sits at the heart of the North Umpqua River corridor and Oregon's oldest wine growing region. The city's outdoor recreation culture, agricultural connections, and growing tourism create distinctive international passport demand. Here are the patterns we see most from Douglas County clients.",
    cards: [
      {
        icon: "ri-map-pin-2-line",
        title: "British Columbia Hunting, Fishing & Outdoor Recreation",
        subtitle: "Umpqua Valley's most common international travel driver — BC hunting and fishing expeditions",
        description: "Roseburg and the Umpqua Valley have a strong hunting and fishing culture — and British Columbia is the most common international destination for Douglas County outdoor travelers. BC elk, mule deer, black bear, and steelhead fishing trips are booked months in advance through outfitters. Passport checks often happen only when packing lists come out a week before departure. Douglas County hunters should check passport validity the same day they book their BC outfitter.",
        tip: "Book your BC license and outfitter, then immediately check your passport expiration date. If it expires within 18 months, renew it now with Fast Passport Center. A 10-day expedited renewal is far less stressful than discovering expiration the day before departure.",
      },
      {
        icon: "ri-cup-line",
        title: "Umpqua Valley Wine Industry — France, Italy, Germany Travel",
        subtitle: "Oregon's oldest wine region sends professionals to European vineyards for partnerships and education",
        description: "The Umpqua Valley AVA is Oregon's oldest wine growing region, and Roseburg's wine community has growing international connections — Burgundy vineyard partnerships, Italian wine education trips, and German Riesling region visits. Wine professionals in Roseburg travel to fixed-date harvest visits, wine fairs (Prowein, Vinexpo), and education programs that cannot be rescheduled. Passport expiration discovered three weeks before a Burgundy harvest visit is a crisis — Fast Passport Center resolves it.",
        tip: "Umpqua Valley wine professionals: confirm passport validity the moment you accept an international vineyard invitation or register for an overseas wine program. Harvest visits in September and October have absolutely fixed dates.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "Eugene Airport and Portland International",
        subtitle: "EUG is 65 miles north; PDX is 180 miles north for direct international flights",
        description: "Roseburg residents use Eugene Airport (EUG, 65 miles north via I-5) for regional connections, or Portland International (PDX, 180 miles) for direct international flights. PDX offers direct service to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City. For South American connections, SFO and LAX via Portland are the most accessible options. All international departures require a valid U.S. passport.",
        tip: "For direct transatlantic flights, Portland International (PDX) saves significant connection time versus flying from Eugene. The 3-hour drive from Roseburg to PDX is worth it for a direct Amsterdam or London flight rather than a three-leg itinerary through San Francisco.",
      },
      {
        icon: "ri-sun-cloudy-line",
        title: "Mexico, Costa Rica & Pacific Travel",
        subtitle: "Winter escapes and eco-adventure travel from the Umpqua Valley",
        description: "Roseburg winters are cold and rainy — and the area's retirement community and young families escape to Mexico and Costa Rica from November through April. Puerto Vallarta, Cabo, and the Yucatán are popular destinations. Costa Rica eco-adventure travel is common with Roseburg's outdoor community — Arenal, Manuel Antonio, and the Osa Peninsula. These bookings typically happen in September and October when winter sets in — and passport checks sometimes happen only during packing.",
        tip: "Book your Mexico or Costa Rica winter trip in September, then check your passport immediately. If it expires before May the following year, renew it now. Fast Passport Center handles Roseburg renewals in 3–9 business days.",
      },
    ],
    callout: {
      icon: "ri-tree-line",
      title: "Roseburg — Umpqua Valley's Gateway to International Travel",
      description: "Roseburg is the commercial hub of Douglas County and the Umpqua Valley — serving hunters, anglers, wine industry professionals, outdoor families, and retirees with growing international travel aspirations. The nearest passport agency is 6 hours away in Seattle, making Fast Passport Center the essential service for Douglas County residents who need their passports fast without a massive road trip.",
    },
  },
  destinations: {
    title: "Where Roseburg Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Roseburg and Douglas County clients. The Umpqua Valley's outdoor culture, wine industry, and retirement community create year-round international passport demand.",
    stats: [
      { stat: "380 mi", label: "to Seattle Passport Agency — 6 hours north from Roseburg via I-5" },
      { stat: "65 mi", label: "to Eugene Airport (EUG) — the closest commercial hub for Roseburg travelers" },
      { stat: "BC & MX", label: "top two passport destinations for Roseburg outdoor and winter travel communities" },
      { stat: "24 Hrs", label: "fastest emergency passport processing available for Roseburg residents" },
    ],
    destinations: [
      { name: "British Columbia, Canada", context: "Umpqua Valley's most popular international destination — BC elk hunting, steelhead fishing, Whistler skiing, Vancouver city breaks, and Gulf Islands travel", urgency: "BC licenses and outfitter bookings months ahead; passport expiration discovered only during trip prep", icon: "ri-map-pin-2-line" },
      { name: "Mexico — Cabo, Puerto Vallarta, Riviera Maya", context: "Winter beach escapes for Roseburg retirees and families — November through April Umpqua Valley winter escape tradition", urgency: "September–October Mexico bookings with January–March departures; passports found expired during holiday packing", icon: "ri-sun-cloudy-line" },
      { name: "France, Italy & Germany", context: "Umpqua Valley wine industry travel — Burgundy harvest visits, Prowein trade fair, Italian wine education, and heritage travel from Roseburg area European families", urgency: "Wine harvest dates are fixed in September–October; trade fair registration deadlines require passport submission 6+ weeks ahead", icon: "ri-global-line" },
      { name: "Alaska Cruise from Seattle", context: "Roseburg-area retirees and outdoor families — Inside Passage sailings from Pier 91 with glacier viewing and wildlife excursions", urgency: "Alaska cruise bookings in January–February; passport validity check during April pre-cruise document gathering", icon: "ri-ship-2-line" },
      { name: "Costa Rica & Central America", context: "Eco-adventure travel popular with Roseburg outdoor community — Manuel Antonio, Arenal volcano, and Pacific coast surf and wildlife", urgency: "October–November dry season bookings; passport expiration discovered at flight search time", icon: "ri-leaf-line" },
      { name: "Japan & Pacific Rim", context: "Teaching English abroad programs popular with Umpqua Community College graduates; cultural travel to Japan, Vietnam, and Southeast Asia from Roseburg", urgency: "Teaching program deadlines require passport months before departure; discovered at application registration", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the Umpqua Valley and southern Oregon with the same expedited passport expertise.",
    crossLinks: [
      { name: "Eugene", route: "/eugene" },
      { name: "Medford", route: "/medford" },
      { name: "Grants Pass", route: "/grants-pass" },
    ],
  },
};